import { AnimatePresence, motion } from 'framer-motion';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import numeral from 'numeral';
import type { JSX } from 'react';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import type { Campaign, Targeting, TPredicateMode } from '@feathr/blackbox';
import { AUDIENCE_TARGETING_KINDS, CampaignClass } from '@feathr/blackbox';
import {
  AlertV2 as Alert,
  AsyncDisplay,
  Button,
  CardV2 as Card,
  ContextMenu,
  EAlertV2Type as EAlertType,
  EmptyState,
  Heading,
  SegmentedControl,
  Well,
} from '@feathr/components';
import { Flaggable } from '@feathr/extender/components';
import Glue from '@feathr/extender/components/Glue';
import { AgeDemographics, Interests, Locations } from '@feathr/extender/components/Targetings';
import { SessionCacheProvider } from '@feathr/extender/hooks';
import { useLocalUrl, useStore } from '@feathr/extender/state';
import { getIconForAction } from '@feathr/hooks';
import type { ListResponse } from '@feathr/rachis';
import { useAudienceBreakdown, useFlags } from '@feathr/report_components';

import { type IUniqueEmailsResult } from '../AddWizard.useReconcileUniqueEmails';
import { useText } from './AdWizardAudienceStep.useText';
import AffinityTargeting from './AffinityTargeting';
import EmailListTargeting from './EmailListTargeting';
import EmailListUploadButton from './EmailListTargeting/EmailListUploadButton';
import GeoFenceTargeting from './GeoFenceTargeting';
import LookalikeAudienceTargeting from './LookalikeAudienceTargeting';
import LookalikeTargeting from './LookalikeTargeting';
import SearchKeywordTargeting from './SearchKeywordTargeting';
import SeedSegmentTargeting from './SeedSegmentTargeting';
import SegmentTargeting from './SegmentTargeting';

import * as styles from './AdWizardAudienceStep.css';

interface IProps {
  campaign: Campaign;
  targetingsList: ListResponse<Targeting>;
  emailValidation: IUniqueEmailsResult;
}

export function getTargetingKind(campaignClass: CampaignClass): string {
  const targetingKinds: Partial<Record<CampaignClass, string>> = {
    [CampaignClass.Segment]: 'segment',
    [CampaignClass.Lookalike]: 'lookalike',
    [CampaignClass.SeedSegment]: 'lookalike',
    [CampaignClass.Affinity]: 'lookalike',
    // Email list upload button specifies the email_list kind.
    // This allows the use of segments on the same page.
    [CampaignClass.EmailList]: 'segment',
    [CampaignClass.EmailListFacebook]: 'segment',
    [CampaignClass.Search]: 'search',
    [CampaignClass.MobileGeoFencing]: 'geofence',
    [CampaignClass.MobileGeoFenceRetargeting]: 'geo_audience',
    [CampaignClass.Facebook]: 'segment',
    [CampaignClass.Omni]: 'segment',
  };

  return targetingKinds[campaignClass] ?? 'segment';
}

const NUM_RECOMMENDED_EMAILS_FACEBOOK = 10000;
const MIN_RECOMMENDED_AUDIENCE_SIZE = 1000;

function AdWizardAudienceStepContent({
  campaign,
  targetingsList,
  emailValidation,
}: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const { Targetings } = useStore();
  const { numTargetedReachableUsers, ...audienceData } = useAudienceBreakdown({
    targetings: targetingsList,
    campaign,
  });
  const cls = campaign.get('_cls');
  const targetingKind = getTargetingKind(cls);
  const localUrl = useLocalUrl();
  const { description, helpDeskUrl } = useText({ cls });
  const { hasBuildReachAwarenessPP } = useFlags();
  const targetings = targetingsList.models;

  const {
    isGeofencingCampaign,
    isSegmentCampaign,
    isEmailMappingCampaign,
    isDisabledCampaignState,
    isAffinityCampaign,
    isFacebook,
    isOmniCampaign,
    isOmniReachCampaign,
  } = campaign;
  const isDisabledCampaignClass = isGeofencingCampaign || isEmailMappingCampaign;
  // Default to match_any for meta
  const mode = campaign.get('mode') ?? 'match_any';
  const {
    numUniqueEmails,
    hasEnoughUniqueEmails,
    remainingEmails,
    isLoading: isLoadingNumUniqueEmails,
  } = emailValidation;

  const removeTargeting = useCallback(
    (targeting: Targeting) => {
      if (targeting.isEphemeral) {
        targeting.collection!.remove(targeting.id);
        targetings.remove(targeting);
      } else {
        targeting.set({ is_archived: true });
      }
    },
    [targetings],
  );

  const onClick = useCallback(() => {
    const model = Targetings.create({
      parent: campaign.get('id'),
      kind: targetingKind,
      included: true,
      group: mode === 'match_all' ? Date.now() : 0,
    });

    runInAction(() => {
      targetings.push(model);
    });
  }, [targetings, mode, campaign, cls, Targetings]);

  const searchTargetings = targetings.filter((t) => {
    return t.get('kind') === 'search' && !t.get('is_archived');
  });

  const filteredTargetings = targetings.filter(
    (t) => !t.get('is_archived') && AUDIENCE_TARGETING_KINDS.includes(t.get('kind')),
  );

  const isReconcilingEmails = isEmailMappingCampaign && isLoadingNumUniqueEmails;
  const hasEmptyTargetings = filteredTargetings.length === 0;

  const numTotalTargeted = isEmailMappingCampaign
    ? numUniqueEmails ?? 0
    : numTargetedReachableUsers ?? 0;
  const formattedNumTotalTargeted = numeral(numTotalTargeted).format('0,0');
  const labelTotalTargeted = isEmailMappingCampaign
    ? t('Total unique email addresses')
    : t('Total reachable people');

  const addTargetButton = (
    <Button
      disabled={
        (isDisabledCampaignState && isDisabledCampaignClass) ||
        (campaign.get('_cls') === CampaignClass.Search && searchTargetings.length >= 1) ||
        isReconcilingEmails
      }
      key={'add'}
      name={'add_target'}
      onClick={onClick}
      prefix={getIconForAction('add')}
    >
      {t('Add target')}
    </Button>
  );

  const handleChangeAudienceGroup = useCallback(
    (value?: string) => {
      if (!value) {
        return;
      }
      campaign.set({ mode: value as TPredicateMode });
      // Update all existing targetings when mode changes
      runInAction(() => {
        filteredTargetings.forEach((targeting, index) => {
          if (value === 'match_all') {
            targeting.set({ group: Date.now() + index });
          } else {
            targeting.set({ group: 0 });
          }
        });
      });
    },
    [filteredTargetings],
  );

  const addTargetButtonEmailMapping = (
    <ContextMenu
      buttonText={t('Add target')}
      disabled={isReconcilingEmails}
      position={'bottom-start'}
      suffix={getIconForAction('arrowDown')}
    >
      <EmailListUploadButton
        campaign={campaign}
        onRemove={removeTargeting}
        targetings={targetings}
      />
      {!isFacebook && <ContextMenu.Item onClick={onClick}>{t('Select a Group')}</ContextMenu.Item>}
    </ContextMenu>
  );

  const targetButton = isEmailMappingCampaign ? addTargetButtonEmailMapping : addTargetButton;
  const isNotReconcilingEmails = !isLoadingNumUniqueEmails && numUniqueEmails;
  const showModeSegmentedControl = (isAffinityCampaign || isSegmentCampaign) && !isFacebook;
  const isOmniReachCampaignWithFlag = (isOmniReachCampaign && hasBuildReachAwarenessPP) ?? false;

  // Pixl Plus, Retargeting or email mapping
  const isRetargetingOrEmailMappingCampaign =
    isEmailMappingCampaign || (isSegmentCampaign && !isOmniCampaign);

  const isLoading = audienceData.isLoading;

  // Show small audience alert for email mapping and retargeting campaigns
  const showSmallAudienceAlert =
    isRetargetingOrEmailMappingCampaign &&
    numTotalTargeted < MIN_RECOMMENDED_AUDIENCE_SIZE &&
    !isFacebook &&
    !hasEmptyTargetings &&
    !isLoadingNumUniqueEmails;

  const showOmniSmallAudienceAlert =
    isOmniCampaign &&
    numTargetedReachableUsers < MIN_RECOMMENDED_AUDIENCE_SIZE &&
    !hasEmptyTargetings;

  const targetsCardWidth = isSegmentCampaign || isAffinityCampaign ? 'wide' : 'narrow';

  return (
    <>
      {isOmniReachCampaignWithFlag ? (
        <>
          <Locations campaign={campaign} targetings={targetings} />
          <AgeDemographics campaign={campaign} targetings={targetings} />
          <Interests campaign={campaign} targetings={targetings} />
        </>
      ) : (
        <>
          <Card
            contentClassName={isOmniCampaign ? styles.left : ''}
            name={'targeting_groups_card'}
            // Targets with groups need extra space to build predicates
            width={isOmniCampaign ? 'full' : targetsCardWidth}
          >
            <Card.Header
              description={
                <>
                  {description}
                  {helpDeskUrl}
                </>
              }
              title={t('People')}
            />
            <Card.Content addVerticalGap={true}>
              {showModeSegmentedControl && (
                <SegmentedControl
                  data={[
                    { label: t('Any of the following'), value: 'match_any' },
                    { label: t('All of the following'), value: 'match_all' },
                  ]}
                  fullWidth={false}
                  name={'mode'}
                  onChange={handleChangeAudienceGroup}
                  value={mode}
                />
              )}

              {isGeofencingCampaign && (
                <Alert
                  className={styles.alert}
                  title={t('Geofence locations within the EU are no longer available due to GDPR.')}
                  type={EAlertType.warning}
                >
                  <a
                    href={
                      'https://help.feathr.co/hc/en-us/articles/360039271953-Data-Protection-Compliance-with-Feathr'
                    }
                    target={'_blank'}
                  >
                    {t('Learn more about GDPR Compliance')}
                  </a>
                </Alert>
              )}
              {filteredTargetings.map((targeting: Targeting, index: number): JSX.Element => {
                if (isSegmentCampaign) {
                  const isLast = index === filteredTargetings.length - 1;
                  return (
                    <React.Fragment key={targeting.listId}>
                      <SegmentTargeting
                        campaign={campaign}
                        key={targeting.listId}
                        onRemove={removeTargeting}
                        showFiltersInModal={true}
                        targeting={targeting}
                        targetings={targetings}
                      />
                      <Glue isLastItem={isLast} mode={mode} suppressGap={true} t={t} />
                    </React.Fragment>
                  );
                }
                if (cls === CampaignClass.Lookalike) {
                  return (
                    <LookalikeTargeting
                      campaign={campaign}
                      key={targeting.listId}
                      onRemove={removeTargeting}
                      targeting={targeting}
                    />
                  );
                }
                if (cls === CampaignClass.SeedSegment) {
                  return (
                    <SeedSegmentTargeting
                      campaign={campaign}
                      key={targeting.listId}
                      onRemove={removeTargeting}
                      targeting={targeting}
                    />
                  );
                }
                if (cls === CampaignClass.Affinity) {
                  const isLast = index === filteredTargetings.length - 1;
                  return (
                    <React.Fragment key={targeting.listId}>
                      <AffinityTargeting
                        campaign={campaign}
                        onRemove={removeTargeting}
                        targeting={targeting}
                      />
                      <Glue isLastItem={isLast} mode={mode} suppressGap={true} t={t} />
                    </React.Fragment>
                  );
                }
                if (isEmailMappingCampaign) {
                  return (
                    <React.Fragment key={targeting.listId}>
                      {targeting.get('kind') === 'email_list' && (
                        <EmailListTargeting
                          campaign={campaign}
                          isReconcilingEmails={isLoadingNumUniqueEmails}
                          onRemove={removeTargeting}
                          targeting={targeting}
                        />
                      )}
                      {targeting.get('kind') === 'segment' && (
                        <SegmentTargeting
                          campaign={campaign}
                          isReconcilingEmails={isLoadingNumUniqueEmails}
                          onRemove={removeTargeting}
                          showFiltersInModal={true}
                          targeting={targeting}
                          targetings={targetings}
                        />
                      )}
                    </React.Fragment>
                  );
                }
                if (cls === CampaignClass.Search) {
                  return (
                    <SearchKeywordTargeting
                      campaign={campaign}
                      key={targeting.listId}
                      onRemove={removeTargeting}
                      targeting={targeting}
                    />
                  );
                }
                if (isGeofencingCampaign) {
                  return (
                    <GeoFenceTargeting
                      campaign={campaign}
                      key={targeting.listId}
                      onRemove={removeTargeting}
                      targeting={targeting}
                    />
                  );
                }
                throw new Error('Unexpected campaign class in AdWizardAudienceStep');
              })}

              {isNotReconcilingEmails && !hasEnoughUniqueEmails ? (
                <Alert
                  className={styles.alert}
                  description={t('Add at least {{num}} more unique email addresses to proceed.', {
                    count: remainingEmails,
                    num: numeral(remainingEmails).format('0,0'),
                  })}
                  title={t('{{num}} unique email addresses have been added.', {
                    count: numUniqueEmails,
                    num: numeral(numUniqueEmails).format('0,0'),
                  })}
                  type={EAlertType.danger}
                />
              ) : (
                isFacebook &&
                hasEnoughUniqueEmails &&
                numUniqueEmails &&
                numUniqueEmails < NUM_RECOMMENDED_EMAILS_FACEBOOK && (
                  <Alert
                    // We really need to remove the margin bottom on our alerts
                    className={styles.alert}
                    description={t(
                      'Feathr recommends {{emailCountRecommendation}} unique email address to ensure good results from the email mapping process.',
                      {
                        count: numUniqueEmails,
                        emailCountRecommendation: numeral(NUM_RECOMMENDED_EMAILS_FACEBOOK).format(
                          '0,0',
                        ),
                      },
                    )}
                    title={t(
                      'This campaign may not perform well with {{emailCountProvided}} unique email addresses.',
                      {
                        count: numUniqueEmails,
                        emailCountProvided: numeral(numUniqueEmails).format('0,0'),
                      },
                    )}
                    type={EAlertType.info}
                  />
                )
              )}

              <AnimatePresence>
                {showOmniSmallAudienceAlert && (
                  <motion.div
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    initial={{ opacity: 0 }}
                    transition={{ duration: 0.3, ease: 'easeOut' }}
                  >
                    <Alert
                      className={styles.alert}
                      description={t(
                        'Pixl Plus delivers strongest results with 1,000+ contacts. Expanding your audience will help you reach more people and achieve better outcomes.',
                      )}
                      name={'target_more_alert'}
                      title={t('Target More Reachable People')}
                      type={EAlertType.info}
                    >
                      <a href={localUrl('data/imports')} target={'_blank'}>
                        {t('Import more contacts')}
                      </a>
                    </Alert>
                  </motion.div>
                )}
              </AnimatePresence>

              <AnimatePresence>
                {showSmallAudienceAlert && (
                  <motion.div
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    initial={{ opacity: 0 }}
                    transition={{ duration: 0.3, ease: 'easeOut' }}
                  >
                    <Alert
                      className={styles.alert}
                      description={t(
                        'Your target audience contains less than 1,000 unique {{type}}. Campaigns with smaller audiences may not spend their full budget effectively, as ad campaigns perform better with audiences of at least 1,000 people. Consider combining multiple groups to improve campaign performance.',
                        {
                          type: isEmailMappingCampaign ? t('email addresses') : t('cookies'),
                          count: numTargetedReachableUsers,
                        },
                      )}
                      name={'small_audience_alert'}
                      title={t('Small Audience Size Detected')}
                      type={EAlertType.info}
                    />
                  </motion.div>
                )}
              </AnimatePresence>

              {hasEmptyTargetings ? (
                <EmptyState
                  description={t('Add a target to get started')}
                  label={t('No targets added')}
                  theme={'neutral'}
                >
                  {targetButton}
                </EmptyState>
              ) : (
                targetButton
              )}

              {(isRetargetingOrEmailMappingCampaign || isOmniCampaign) && (
                <Well label={labelTotalTargeted} name={'total_targeted'}>
                  <AsyncDisplay
                    isLoading={isLoading}
                    // Match the line height of the heading
                    skeleton={{ options: { paragraphs: 1, height: 28, width: 100 } }}
                  >
                    <Heading mantineComponent={'span'} name={'total_targeted_value'} variant={'h4'}>
                      {/* Force reachable people to 0 so we don't have to call breakdown again for 0 data  */}
                      {filteredTargetings.length !== 0 ? formattedNumTotalTargeted : 0}
                    </Heading>
                  </AsyncDisplay>
                </Well>
              )}
            </Card.Content>
          </Card>
          {isOmniCampaign && (
            <SessionCacheProvider>
              <Flaggable devOverride={false} featureName={'showLookalikeAudienceTargeting'}>
                <LookalikeAudienceTargeting
                  campaign={campaign}
                  isLoading={isLoading}
                  numReachableUsers={filteredTargetings.length !== 0 ? numTotalTargeted : 0}
                  targetings={targetings}
                />
              </Flaggable>
            </SessionCacheProvider>
          )}
        </>
      )}
    </>
  );
}

export default observer(AdWizardAudienceStepContent);
